import { Injectable } from "@angular/core";
import { ApplicationContext, CompanyUser } from "../models/application-context.model";
import { Observable, Subject, catchError, map, of, switchMap, merge, forkJoin, tap } from "rxjs";
import {
  AuthenticationService,
  CompanyProductLicense,
  OrganizationServiceDto,
  User,
  UserCompany,
} from "./auth.service";
import { filterOrganizationServicesByChartsV3License } from "../utils/organization-services-license.util";
import {
  ChecklistQuestion,
  EnumerationItem,
  ProcedureRoomInfo,
  Provider,
  Resource,
} from "../models/lookup.model";
import { LookupService } from "./lookup.service";
import { DataService } from "./data.service";
import { ApplicationCacheService } from "./application-cache.service";
import { setLaunchRequestedCompanyId } from "../utils/clinic-launch-context.util";

@Injectable({
  providedIn: "root",
})
export class ApplicationContextService {
  private _applicationContext: ApplicationContext;
  private _contextChangedSubject: Subject<ApplicationContext> =
    new Subject<ApplicationContext>();

  constructor(
    private readonly _authService: AuthenticationService,
    private readonly _lookupService: LookupService,
    private readonly _dataService: DataService,
    private readonly _applicationCacheService: ApplicationCacheService
  ) {}

  private isReferralDataLoaded = false;
  private isReferralDataLoading = false;
  private procedureRoomGlobalLookupsLoaded = false;
  private procedureRoomGlobalLookupsLoading = false;

  get context(): Observable<ApplicationContext> {
    // If context exists, return a stream that emits the current context and future updates
    if (this._applicationContext) {
      return merge(
        of(this._applicationContext),
        this._contextChangedSubject.asObservable()
      );
    }
    
    // Initialize context and then merge with future updates
    return this.initializeApplicationContext().pipe(
      map((context) => {
        this._applicationContext = context;
        this.syncPhiOrganizationHeaderWithContext(context);
        this._contextChangedSubject.next(context);
        this.loadReferralLookupData();
        
        // Note: initializeGlobalData is no longer called here
        // Templates, prompts, and questions will be loaded by RouteDataLoaderService
        // only on specific routes (/referrals, /referral-details, /encounters/:id)
        
        return context;
      }),
      switchMap((initialContext) => 
        merge(
          of(initialContext),
          this._contextChangedSubject.asObservable()
        )
      )
    );
  }

  get contextChanged$(): Subject<ApplicationContext> {
    return this._contextChangedSubject;
  }

  /** Keep session launch org aligned with the signed-in workspace (PHI `X-Organization-Id`). */
  private syncPhiOrganizationHeaderWithContext(
    context: ApplicationContext | null | undefined
  ): void {
    const companyId = context?.currentlyLoggedInUser?.companyId?.trim();
    if (companyId) {
      setLaunchRequestedCompanyId(companyId);
    }
  }

  resetContext() {
    this._applicationContext = null;
    this.isReferralDataLoaded = false;
    this.isReferralDataLoading = false;
    this.procedureRoomGlobalLookupsLoaded = false;
    this.procedureRoomGlobalLookupsLoading = false;
    this._lookupService.clearAllUsersCache();
    this._lookupService.clearProvidersAndResourcesCache();
    // Patient, encounter, and Engage selection are organization-scoped. Leaving them
    // in memory shows the previous company's patient in Activity after a company switch.
    this._applicationCacheService.clearSelection();
  }

  /**
   * Procedure room dispense supplies / snacks-meals dropdowns:
   * GET `api/v1/lookup/global/supplies` and `.../snacks` (cached for the session).
   */
  loadProcedureRoomGlobalLookups(): void {
    if (this.procedureRoomGlobalLookupsLoaded || this.procedureRoomGlobalLookupsLoading) {
      return;
    }
    if (!this._applicationContext) {
      return;
    }
    this.procedureRoomGlobalLookupsLoading = true;
    forkJoin({
      supplies: this._lookupService.getGlobalSupplies(),
      snacks: this._lookupService.getGlobalSnacks(),
    }).subscribe({
      next: ({ supplies, snacks }) => {
        if (this._applicationContext) {
          this._applicationContext.supplies = supplies ?? [];
          this._applicationContext.snacks = snacks ?? [];
          this._contextChangedSubject.next(this._applicationContext);
        }
        this.procedureRoomGlobalLookupsLoaded = true;
        this.procedureRoomGlobalLookupsLoading = false;
      },
      error: () => {
        this.procedureRoomGlobalLookupsLoading = false;
      },
    });
  }

  /** Current app context if already initialized (e.g. after login); otherwise null. */
  get contextSnapshot(): ApplicationContext | null {
    return this._applicationContext ?? null;
  }

  /**
   * Refetches GET api/v1/auth/currentUser and replaces `currentlyLoggedInUser` in the cached context
   * (e.g. after POST .../users/delegations so `delegatedUsers` / `delegatedByUsers` are up to date).
   */
  refreshLoggedInUser(): Observable<User> {
    return this._authService.getLoggedInUser().pipe(
      tap((user) => {
        if (this._applicationContext && user?.id) {
          this._applicationContext.currentlyLoggedInUser = user;
          this._contextChangedSubject.next(this._applicationContext);
        }
      })
    );
  }

  /**
   * Get firstName and lastName for a userId from cached company users.
   */
  getUserNames(userId: string): { firstName: string; lastName: string } | null {
    const map = this._applicationContext?.companyUsersMap;
    const user = userId ? map[userId] : null;
    if (!user) return null;
    return { firstName: user.firstName ?? '', lastName: user.lastName ?? '' };
  }

  /**
   * Update the application context with providers, resources, and questions data
   * This is called by RouteDataLoaderService after loading the route-specific data
   */
  updateContextWithRouteData(
    providers: Provider[], 
    resources: Resource[], 
    checklistQuestions: ChecklistQuestion[],
    forceStopRedirect: boolean = false
  ): void {
    if (this._applicationContext) {
      // Extract different question types from checklist questions
      const pqrstaQuestions = checklistQuestions.filter(q => q.area === 'PQRSTA');
      const opqrstQuestions = checklistQuestions.filter(q => q.area === 'OPQRST');
      const historyQuestions = checklistQuestions.filter(q => q.category === 'History');

      this._applicationContext.providers = providers;
      this._applicationContext.resources = resources;
      this._applicationContext.checklistQuestions = checklistQuestions;
      this._applicationContext.pqrstaQuestions = pqrstaQuestions;
      this._applicationContext.opqrstQuestions = opqrstQuestions;
      this._applicationContext.historyQuestions = historyQuestions;

      if (providers?.length && resources?.length) {
        this.isReferralDataLoaded = true;
        this.isReferralDataLoading = false;
      }

      // Notify subscribers of the context change
      if (!forceStopRedirect) {
        this._contextChangedSubject.next(this._applicationContext);
      }
    }
  }

  private initializeApplicationContext = (): Observable<ApplicationContext> => {
    // TEMPORARY: Set to true to override isScheduleResource for testing
    const TEMP_OVERRIDE_IS_SCHEDULE_RESOURCE = false;
    
    return this._authService.getLoggedInUser().pipe(
      catchError((error) => of(error)),
      switchMap((user: User) => {
        // TEMPORARY: Override isScheduleResource for testing
        if (TEMP_OVERRIDE_IS_SCHEDULE_RESOURCE) {
          user.isScheduleResource = true;
        }
        
        return this._authService.getUserCompanies(user.id).pipe(
          catchError(() => of([] as UserCompany[])),
          switchMap((userCompanies: UserCompany[]) =>
            this._authService.getUserLicenses().pipe(
              catchError((error) => of(error)),
              switchMap((licenses: CompanyProductLicense[]) =>
                forkJoin({
                  procedureRoomInfo: this._lookupService.getProcedureRoomsInfo().pipe(catchError(() => of([]))),
                  allUsers: this._lookupService.allUsers().pipe(catchError(() => of([] as CompanyUser[]))),
                  rawOrganizationServices: this._authService
                    .getServices()
                    .pipe(catchError(() => of([] as OrganizationServiceDto[]))),
                }).pipe(
                  map(({ procedureRoomInfo, allUsers, rawOrganizationServices }) => {
                    const companyUsersMap: Record<string, CompanyUser> = {};
                    (allUsers || []).forEach((u) => { companyUsersMap[u.id] = u; });
                    const licenseNames = Array.isArray(licenses)
                      ? licenses.map((l) => l.name)
                      : [];
                    const rawList = Array.isArray(rawOrganizationServices)
                      ? rawOrganizationServices
                      : [];
                    const organizationServicesCatalog = rawList.map(
                      ({ id, serviceName, serviceType }) => ({
                        id,
                        serviceName,
                        serviceType,
                      })
                    );
                    const organizationServices = filterOrganizationServicesByChartsV3License(
                      rawList,
                      licenseNames
                    ).map(({ id, serviceName, serviceType }) => ({
                      id,
                      serviceName,
                      serviceType,
                    }));
                    return {
                    currentlyLoggedInUser: user,
                    userCompanies,
                    companyUsersMap,
                    allAvailableUsers: allUsers || [],
                    providers: [],
                    resources: [],
                    checklistQuestions: [],
                    pqrstaQuestions: [],
                    opqrstQuestions: [],
                    historyQuestions: [],
                    snacks: [],
                    supplies: [],
                    companyLicenses: licenseNames,
                    organizationServices,
                    organizationServicesCatalog,
                    hasNoBillingPhysiciansForCharts:
                      Array.isArray(licenses) &&
                      licenses.some((l) => l.name === "charts" && l.activeBillingPhysicians === 0),
                    locations: (procedureRoomInfo || []).map((loc: ProcedureRoomInfo) => ({
                      displayName: loc.name,
                      value: loc.id,
                    })),
                    ...this.getProcedureRoomInfo(procedureRoomInfo || []),
                  };
                  })
                )
              )
            )
          )
        );
      })
    );
  };

  private loadReferralLookupData(): void {
    if (this.isReferralDataLoaded || this.isReferralDataLoading) {
      return;
    }
    const hasProviders = (this._applicationContext?.providers?.length ?? 0) > 0;
    const hasResources = (this._applicationContext?.resources?.length ?? 0) > 0;
    if (hasProviders && hasResources) {
      this.isReferralDataLoaded = true;
      return;
    }

    this.isReferralDataLoading = true;

    forkJoin({
      providers: this._lookupService.getProviders(),
      resources: this._lookupService.getResources(),
    }).subscribe({
      next: ({ providers, resources }) => {
        if (this._applicationContext) {
          this._applicationContext.providers = providers;
          this._applicationContext.resources = resources;
          this._contextChangedSubject.next(this._applicationContext);
        }

        this.isReferralDataLoaded = true;
        this.isReferralDataLoading = false;
      },
      error: () => {
        this.isReferralDataLoading = false;
      }
    });
  }

  private getProcedureRoomInfo(procedureRoomInfo: ProcedureRoomInfo[]) {
    const info =
      Array.isArray(procedureRoomInfo) && procedureRoomInfo.length > 0
        ? procedureRoomInfo.map((pr) => ({
            name: pr.name,
            id: pr.id,
            noOfBeds: Number(pr.noOfBeds) || 1,
            postProcedure: Number(pr.postProcedure) || 2,
            preProcedure: Number(pr.preProcedure) || 2,
            procedure: Number(pr.procedure) || 2,
            procedureRooms: Number(pr.procedureRooms) || 2,
            slots: pr.slots || [],
          }))[0]
        : new ProcedureRoomInfo();

    const beds: EnumerationItem[] = [];
    for (let i = 0; i < info.noOfBeds; i++) {
      beds.push({ displayName: `Bed ${i + 1}`, value: `${i + 1}` });
    }
    const procedureRooms: EnumerationItem[] = [];
    for (let i = 0; i < info.procedureRooms; i++) {
      procedureRooms.push({
        displayName: `Room ${i + 1}`,
        value: `${i + 1}`,
      });
    }

    return {
      id: info.id,
      name: info.name,
      beds,
      procedureRooms,
      postProcedureDuration: (Number(info.postProcedure) || 2) * 60,
      preProcedureDuration: (Number(info.preProcedure) || 2) * 60,
      procedureDuration: (Number(info.procedure) || 2) * 60,
      slots: info.slots,
    };
  }

  /**
   * Engage scheduling payloads include a `service` field.
   * Account services for dropdowns live on {@link ApplicationContext.organizationServices}.
   */
  resolveEncounterServiceId(serviceNameOrId: string): string {
    const raw = (serviceNameOrId || "").trim();
    if (!raw) {
      return "";
    }

    const services = this.getOrganizationServicesForLookup();
    const byId = services.find((s) => (s.id || "").trim() === raw);
    if (byId) {
      return byId.id;
    }

    const lower = raw.toLowerCase();
    const byName = services.find(
      (s) => (s.serviceName || "").trim().toLowerCase() === lower
    );
    return byName?.id || raw;
  }

  resolveEncounterServiceDisplayName(serviceNameOrId: string): string {
    const raw = (serviceNameOrId || "").trim();
    if (!raw) {
      return "";
    }

    const services = this.getOrganizationServicesForLookup();
    const byId = services.find((s) => (s.id || "").trim() === raw);
    if (byId?.serviceName) {
      return byId.serviceName;
    }

    const lower = raw.toLowerCase();
    const byName = services.find(
      (s) => (s.serviceName || "").trim().toLowerCase() === lower
    );
    if (byName?.serviceName) {
      return byName.serviceName;
    }

    return raw;
  }

  private getOrganizationServicesForLookup(): Pick<
    OrganizationServiceDto,
    "id" | "serviceName" | "serviceType"
  >[] {
    const catalog = this._applicationContext?.organizationServicesCatalog;
    if (catalog?.length) {
      return catalog;
    }
    return this._applicationContext?.organizationServices ?? [];
  }
}
